import { get } from 'lodash-es';
import { FieldValues, Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { AnyDocument } from '../../../types';
import { FlatFormBlockConfig } from '../types/FlatFormBlockConfig';
import { FormResolverConfig } from '../types/FormResolverConfig';
import { NamespacedFormBlockConfig } from '../types/NamespacedFormBlockConfig';
import { buildBlockSchema } from './buildBlockSchema';
import { buildNamespacedShape } from './buildNamespacedShape';

export const handleResolverFromConfig =
  <TFieldValues extends FieldValues>(
    formResolverConfig: FormResolverConfig,
    rootDocument: AnyDocument | undefined,
  ): Resolver<TFieldValues> =>
  async (values: TFieldValues, context: unknown, options: any) => {
    if (!Array.isArray(formResolverConfig)) {
      return yupResolver(formResolverConfig)(values, context, options);
    }

    const flatConfigs = formResolverConfig.filter(
      (c): c is FlatFormBlockConfig => !('namespace' in c),
    );
    const namespacedConfigs = formResolverConfig.filter(
      (c): c is NamespacedFormBlockConfig => 'namespace' in c,
    );

    const namespacedShape =
      namespacedConfigs.length > 0
        ? buildNamespacedShape(namespacedConfigs, rootDocument, values)
        : {};

    const flatShape = flatConfigs.reduce(
      (shape, { formFieldBlocks }) => ({
        ...shape,
        ...buildBlockSchema(formFieldBlocks, rootDocument, (key) =>
          get(values, key),
        ).fields,
      }),
      {},
    );

    return yupResolver(
      yup.object().shape({ ...namespacedShape, ...flatShape }),
    )(values, context, options);
  };
